import React, { useEffect, useMemo, useState } from "react";
import { Search, Download, ChevronDown, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { API } from "@/lib/api";
import { downloadInvoicePdf } from "@/lib/invoice";

export const ORDER_STATUSES = [
  { key: "pending", label: "Pending" },
  { key: "awaiting_payment", label: "Awaiting Payment" },
  { key: "paid", label: "Paid" },
  { key: "ready_for_collection", label: "Ready for Collection" },
  { key: "out_for_delivery", label: "Out for Delivery" },
  { key: "completed", label: "Completed" },
  { key: "cancelled", label: "Cancelled" },
];

const STATUS_COLORS = {
  pending: "bg-neutral-100 text-neutral-700 border-neutral-300",
  awaiting_payment: "bg-amber-50 text-amber-800 border-amber-300",
  paid: "bg-emerald-50 text-emerald-800 border-emerald-300",
  ready_for_collection: "bg-sky-50 text-sky-800 border-sky-300",
  out_for_delivery: "bg-indigo-50 text-indigo-800 border-indigo-300",
  completed: "bg-brand-surface text-brand-accent border-brand-accent",
  cancelled: "bg-red-50 text-red-700 border-red-300",
};

const DELIVERY_LABEL = {
  collection: "Collection · Love Beach",
  local_delivery: "Nassau · $5",
  family_island: "Family Island · Quoted",
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [busyId, setBusyId] = useState(null);
  const [expanded, setExpanded] = useState({});

  const loadStats = () => api.get("/admin/orders/stats").then((r) => setStats(r.data)).catch(() => {});

  const loadOrders = () => {
    const p = new URLSearchParams();
    if (q.trim()) p.set("q", q.trim());
    if (status !== "all") p.set("status", status);
    return api.get(`/admin/orders?${p.toString()}`).then((r) => setOrders(r.data)).catch(() => {});
  };

  useEffect(() => { loadStats(); }, []);
  useEffect(() => {
    const h = setTimeout(loadOrders, 250);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, status]);

  const updateStatus = async (id, newStatus) => {
    setBusyId(id);
    try {
      await api.patch(`/admin/orders/${id}/status`, { status: newStatus });
      toast.success("Status updated. Customer notified by email.");
      await Promise.all([loadOrders(), loadStats()]);
    } catch (err) {
      toast.error("Could not update status.");
    } finally { setBusyId(null); }
  };

  const exportCSV = async () => {
    try {
      const token = localStorage.getItem("island_token");
      const res = await fetch(`${API}/admin/orders/export.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `island-studio-orders-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded.");
    } catch { toast.error("Could not export."); }
  };

  const kpis = useMemo(() => {
    if (!stats) return [];
    return [
      { key: "orders", label: "All Orders", value: stats.order_count },
      { key: "sales", label: "Total Sales (Paid+)", value: `$${stats.total_sales.toFixed(2)}` },
      { key: "vat", label: "VAT Collected", value: `$${stats.total_vat.toFixed(2)}` },
      { key: "pipeline", label: "Open Pipeline", value: `$${stats.pipeline_value.toFixed(2)}` },
    ];
  }, [stats]);

  return (
    <div data-testid="admin-orders" className="space-y-8">
      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <div key={k.key} data-testid={`kpi-${k.key}`} className="border border-brand-border p-5">
            <p className="overline text-[10px]">{k.label}</p>
            <p className="font-serif text-3xl text-brand-dark mt-3">{k.value}</p>
          </div>
        ))}
      </div>

      {/* Status distribution */}
      {stats && (
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="filter-status-all"
            onClick={() => setStatus("all")}
            className={`px-3 py-1.5 text-xs uppercase tracking-widest border ${status === "all" ? "border-brand-accent text-brand-accent" : "border-brand-border text-brand-neutral hover:text-brand-accent"}`}
          >
            All · {stats.order_count}
          </button>
          {ORDER_STATUSES.map((s) => (
            <button
              key={s.key}
              data-testid={`filter-status-${s.key}`}
              onClick={() => setStatus(s.key)}
              className={`px-3 py-1.5 text-xs uppercase tracking-widest border ${status === s.key ? "border-brand-accent text-brand-accent" : "border-brand-border text-brand-neutral hover:text-brand-accent"}`}
            >
              {s.label} · {stats.counts?.[s.key] ?? 0}
            </button>
          ))}
        </div>
      )}

      {/* Search + export */}
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div className="flex items-center gap-3 border-b border-brand-border py-1 flex-1 min-w-[240px] max-w-md">
          <Search strokeWidth={1.5} className="w-4 h-4 text-brand-neutral" />
          <input
            data-testid="orders-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, email or order #"
            className="flex-1 bg-transparent py-2 text-sm outline-none"
          />
        </div>
        <button data-testid="orders-export-csv" onClick={exportCSV} className="btn-outline">
          <Download strokeWidth={1.5} className="w-4 h-4" /> Export CSV
        </button>
      </div>

      {/* Table */}
      <div className="border-t border-brand-border">
        {orders.length === 0 ? (
          <p data-testid="orders-empty" className="text-brand-neutral py-10 text-center">No orders match this view.</p>
        ) : (
          <>
            <div className="hidden md:grid grid-cols-12 gap-3 py-3 border-b border-brand-border text-[10px] uppercase tracking-widest text-brand-neutral">
              <div className="col-span-2">Order</div>
              <div className="col-span-3">Customer</div>
              <div className="col-span-2">Fulfilment</div>
              <div className="col-span-1 text-right">Total</div>
              <div className="col-span-3">Status</div>
              <div className="col-span-1 text-right">Date</div>
            </div>
            <ul className="divide-y divide-brand-border">
              {orders.map((o) => {
                const short = o.id.slice(0, 8).toUpperCase();
                const isOpen = expanded[o.id];
                return (
                  <li key={o.id} data-testid={`order-row-${o.id}`} className="py-4">
                    <div className="grid md:grid-cols-12 gap-3 items-center">
                      <button
                        data-testid={`order-toggle-${o.id}`}
                        onClick={() => setExpanded((e) => ({ ...e, [o.id]: !e[o.id] }))}
                        className="md:col-span-2 text-left font-serif text-brand-dark flex items-center gap-1"
                      >
                        <ChevronDown strokeWidth={1.5} className={`w-4 h-4 text-brand-neutral transition-transform ${isOpen ? "rotate-180" : ""}`} />
                        <span>#{short}</span>
                      </button>
                      <div className="md:col-span-3 text-sm">
                        <p className="text-brand-dark">{o.name}</p>
                        <p className="text-xs text-brand-neutral">{o.email}</p>
                      </div>
                      <div className="md:col-span-2 text-xs text-brand-neutral">
                        {DELIVERY_LABEL[o.delivery_method] || o.delivery_method}
                      </div>
                      <div className="md:col-span-1 md:text-right font-medium text-brand-dark">${o.total.toFixed(2)}</div>
                      <div className="md:col-span-3">
                        <div className="relative inline-block w-full max-w-[220px]">
                          <select
                            data-testid={`status-select-${o.id}`}
                            value={o.status}
                            disabled={busyId === o.id}
                            onChange={(e) => updateStatus(o.id, e.target.value)}
                            className={`w-full appearance-none border ${STATUS_COLORS[o.status] || STATUS_COLORS.pending} py-1.5 pl-3 pr-8 text-xs uppercase tracking-widest cursor-pointer`}
                          >
                            {ORDER_STATUSES.map((s) => (
                              <option key={s.key} value={s.key}>{s.label}</option>
                            ))}
                          </select>
                          <ChevronDown strokeWidth={1.5} className="w-3.5 h-3.5 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none opacity-60" />
                        </div>
                      </div>
                      <div className="md:col-span-1 md:text-right flex items-center gap-2 md:justify-end text-xs text-brand-neutral">
                        <button
                          data-testid={`admin-invoice-${o.id}`}
                          onClick={() =>
                            downloadInvoicePdf({
                              path: `/admin/orders/${o.id}/invoice.pdf`,
                              filename: `island-studio-invoice-${o.id.slice(0, 8).toUpperCase()}.pdf`,
                            })
                          }
                          aria-label="Download invoice PDF"
                          title="Download invoice PDF"
                          className="text-brand-neutral hover:text-brand-accent"
                        >
                          <FileText strokeWidth={1.5} className="w-4 h-4" />
                        </button>
                        <span>{new Date(o.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>

                    {isOpen && (
                      <div data-testid={`order-details-${o.id}`} className="mt-4 ml-6 p-4 border-l border-brand-accent bg-brand-surface text-sm">
                        <div className="grid md:grid-cols-3 gap-6">
                          <div>
                            <p className="overline text-[10px]">Contact</p>
                            <p className="text-brand-dark mt-1">{o.name}</p>
                            <p className="text-brand-neutral">{o.email}</p>
                            <p className="text-brand-neutral">{o.phone || "—"}</p>
                            {o.tin && <p className="text-brand-neutral">TIN: {o.tin}</p>}
                          </div>
                          <div>
                            <p className="overline text-[10px]">Fulfilment</p>
                            <p className="text-brand-dark mt-1">{DELIVERY_LABEL[o.delivery_method]}</p>
                            {o.family_island_name && <p className="text-brand-neutral">Island: {o.family_island_name}</p>}
                            {o.delivery_address && <p className="text-brand-neutral whitespace-pre-wrap">{o.delivery_address}</p>}
                            {o.delivery_tbc && <p className="text-amber-700 text-xs mt-1">Delivery quoted separately</p>}
                          </div>
                          <div>
                            <p className="overline text-[10px]">Totals</p>
                            <div className="mt-1 space-y-0.5 text-brand-neutral">
                              <div className="flex justify-between"><span>Products</span><span>${o.subtotal.toFixed(2)}</span></div>
                              <div className="flex justify-between"><span>Delivery</span><span>${o.delivery_fee.toFixed(2)}{o.delivery_tbc && <em className="not-italic text-brand-accent ml-1">· TBC</em>}</span></div>
                              <div className="flex justify-between pt-1 mt-1 border-t border-brand-border/60"><span>Subtotal (pre-VAT)</span><span>${(o.pre_vat_subtotal ?? (o.subtotal + o.delivery_fee)).toFixed(2)}</span></div>
                              <div className="flex justify-between"><span>VAT (10%)</span><span>${o.vat.toFixed(2)}</span></div>
                              <div className="flex justify-between pt-1 mt-1 border-t border-brand-border text-brand-dark">
                                <span>Total Due</span><span className="text-brand-accent font-medium">${o.total.toFixed(2)}</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="mt-5">
                          <p className="overline text-[10px] mb-2">Items</p>
                          <ul className="divide-y divide-brand-border/60">
                            {o.items.map((it, i) => (
                              <li key={i} className="flex justify-between py-2 text-brand-neutral">
                                <span>{it.qty} × <span className="text-brand-dark">{it.title}</span></span>
                                <span>${(it.price * it.qty).toFixed(2)}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        {o.notes && (
                          <div className="mt-4">
                            <p className="overline text-[10px]">Customer notes</p>
                            <p className="text-brand-neutral mt-1 whitespace-pre-wrap">{o.notes}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
